import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { Topic, TopicCreate, TopicOwner, TopicUpdate } from '@models/marketing/topics/topics.model';
import { SubscriptionListItem } from '@models/subscriptions';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, of, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  deleteEntity,
  getAll,
  guidRegex,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { NewsTopicsStore } from './news-topics/news-topics.store';
import { TopicsService } from './topics.service';

type TopicsState = {
  topics: Topic[];
  selectedTopicId: string | null;
  isLoading: boolean;

  isTopicCreateLoading: boolean;
  isTopicOwnerCreateLoading: boolean;
  isSelectedTopicLoading: boolean;
  subscribers: SubscriptionListItem[];
  isSubscribersLoading: boolean;
  owners: TopicOwner[];
  isOwnersLoading: boolean;
  selectedOwner: TopicOwner | null;
  isSelectedOwnerLoading: boolean;
};

const initialState: TopicsState = {
  topics: [],
  selectedTopicId: null,
  isLoading: false,

  isTopicCreateLoading: false,
  isTopicOwnerCreateLoading: false,
  isSelectedTopicLoading: false,
  subscribers: [],
  isSubscribersLoading: false,
  owners: [],
  isOwnersLoading: false,
  selectedOwner: null,
  isSelectedOwnerLoading: false,
};

const topicsDictionary: StateDictionary<Topic> = {
  entityList: 'topics',
  listLoading: 'isLoading',
  createLoading: 'isTopicCreateLoading',
  selectedEntity: 'selectedTopic',
  selectedEntityLoading: 'isSelectedTopicLoading',
  entityId: 'id',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const TopicsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('topics-store'),
  withComputed(state => ({
    selectedTopic: computed(() => (state.selectedTopicId() ? state.topics().find(topic => topic.id === state.selectedTopicId()) : null)),
  })),
  withMethods((store, topicsService = inject(TopicsService), router = inject(Router), newsTopicsStore = inject(NewsTopicsStore)) => {
    createCustomStateSetOnUrlHandler<TopicsState>('/marketing/topics', {
      selectedTopicId: null,
      selectedOwner: null,
      owners: [],
      subscribers: [],
    })(store);
    createCustomStateSetOnRegexHandler<TopicsState>(new RegExp(`^/marketing/topics/(${guidRegex})/owners$`))(store, {
      selectedOwner: null,
    });

    const repositoryMethods = {
      getAll: getAll<Topic>({
        store,
        service: topicsService,
        dictionary: topicsDictionary,
      }),

      addTopic: addEntity<Topic, TopicCreate>({
        store,
        service: topicsService,
        dictionary: topicsDictionary,
        onSuccessCallback: newTopic => {
          patchState(store, { selectedTopicId: newTopic.id });
          newsTopicsStore.getAll();
          newsTopicsStore.selectTopic(newTopic.id);
          router.navigate(['marketing', 'topics', newTopic.id, 'details']);
        },
      }),

      updateTopic: updateEntity<Topic, TopicUpdate>({
        store,
        service: topicsService,
        dictionary: topicsDictionary,
        onSuccessCallback: updatedTopic => {
          patchState(store, { selectedTopicId: updatedTopic.id });
          newsTopicsStore.selectTopic(updatedTopic.id);
        },
      }),

      deleteTopic: deleteEntity<Topic>({
        store,
        service: topicsService,
        dictionary: topicsDictionary,
        onSuccessCallback: () => {
          router.navigate(['marketing', 'topics']);
        },
      }),

      getTopicSubscribers: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSubscribersLoading: true, subscribers: [] })),
          switchMap(id =>
            topicsService.getSubscribers(id).pipe(
              tap(subscribers => patchState(store, { subscribers, isSubscribersLoading: false })),
              catchError(() => {
                patchState(store, { isSubscribersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      getTopicOwners: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isOwnersLoading: true, owners: [] })),
          switchMap(id =>
            topicsService.getOwners(id).pipe(
              tap(owners => patchState(store, { owners, isOwnersLoading: false })),
              catchError(() => {
                patchState(store, { isOwnersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getTopicOwner: rxMethod<{ topicId: string; ownerId: string }>(
        pipe(
          tap(() => patchState(store, { isSelectedOwnerLoading: true })),
          switchMap(({ topicId, ownerId }) => {
            const existingOwner = store.owners().find(owner => owner.id === ownerId);
            if (existingOwner) {
              patchState(store, { selectedOwner: existingOwner, isSelectedOwnerLoading: false });
              return of(existingOwner);
            }

            return topicsService.getOwner(topicId, ownerId).pipe(
              tap(owner => patchState(store, { selectedOwner: owner, isSelectedOwnerLoading: false })),
              catchError(() => {
                patchState(store, { isSelectedOwnerLoading: false });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      addTopicOwner: rxMethod<{ id: string; ownerEmail: string }>(
        pipe(
          tap(() => patchState(store, { isOwnersLoading: true, isTopicOwnerCreateLoading: true })),
          switchMap(({ id, ownerEmail }) =>
            topicsService.addOwner(id, ownerEmail).pipe(
              tap(owner => {
                const updatedOwners = [...store.owners(), owner].sort((a, b) => a.name.localeCompare(b.name));

                patchState(store, {
                  owners: updatedOwners,
                  selectedOwner: owner,
                  isOwnersLoading: false,
                  isTopicOwnerCreateLoading: false,
                });

                router.navigate(['marketing', 'topics', id, 'owners', owner.id, 'details']);
              }),
              catchError(() => {
                patchState(store, { isOwnersLoading: false, isTopicOwnerCreateLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteTopicOwner: rxMethod<{ topicId: string; ownerId: string }>(
        pipe(
          tap(() => patchState(store, { isSelectedOwnerLoading: true, isOwnersLoading: true })),
          switchMap(({ topicId, ownerId }) =>
            topicsService.deleteOwner(topicId, ownerId).pipe(
              tap(() => {
                patchState(store, {
                  isSelectedOwnerLoading: false,
                  isOwnersLoading: false,
                  selectedOwner: null,
                  owners: store.owners().filter(owner => owner.id !== ownerId),
                });
                router.navigate(['marketing', 'topics', topicId, 'owners']);
              }),
              catchError(() => {
                patchState(store, { isSelectedOwnerLoading: false, isOwnersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      selectTopic: (id: string): void => {
        if (!store.topics().length && !store.isLoading()) repositoryMethods.getAll();

        patchState(store, { selectedTopicId: id });
        newsTopicsStore.selectTopic(id);
      },

      goToTopicActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['marketing', 'topics', id]);
        if (!result) return false;
        patchState(store, { selectedTopicId: id });
        newsTopicsStore.selectTopic(id);
        return result;
      },

      goToTopicOwnerActions: (ownerId: string): void => {
        const topicId = store.selectedTopicId();
        if (topicId) {
          repositoryMethods.getTopicOwner({ topicId, ownerId });
          router.navigate(['marketing', 'topics', topicId, 'owners', ownerId]);
        }
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface TopicsStoreType {
  readonly topics: Signal<Topic[]>;
  readonly selectedTopicId: Signal<string | null>;
  readonly isLoading: Signal<boolean>;
  readonly isTopicCreateLoading: Signal<boolean>;
  readonly isTopicOwnerCreateLoading: Signal<boolean>;
  readonly isSelectedTopicLoading: Signal<boolean>;
  readonly subscribers: Signal<SubscriptionListItem[]>;
  readonly isSubscribersLoading: Signal<boolean>;
  readonly owners: Signal<TopicOwner[]>;
  readonly isOwnersLoading: Signal<boolean>;
  readonly selectedOwner: Signal<TopicOwner | null>;
  readonly isSelectedOwnerLoading: Signal<boolean>;

  readonly selectedTopic: Signal<Topic | null | undefined>;

  readonly getAll: () => void;
  readonly addTopic: (request: TopicCreate) => void;
  readonly updateTopic: (input: { id: string; dto: TopicUpdate }) => void;
  readonly deleteTopic: (id: string) => void;

  readonly getTopicSubscribers: (id: string) => void;

  readonly getTopicOwners: (id: string) => void;
  readonly getTopicOwner: (input: { topicId: string; ownerId: string }) => void;
  readonly addTopicOwner: (input: { id: string; ownerEmail: string }) => void;
  readonly deleteTopicOwner: (input: { topicId: string; ownerId: string }) => void;

  readonly selectTopic: (id: string) => void;
  readonly goToTopicActions: (id: string) => Promise<boolean>;
  readonly goToTopicOwnerActions: (ownerId: string) => void;
}
